// Cellery Soapbox — selection <-> Anchor (CONTRACTS §6).
// Anchors are scoped to the nearest ancestor element with an `id` so they
// survive content above them changing. Falls back to <body> when no id
// ancestor exists. Uses dom-anchor-text-quote (primary) + dom-anchor-text-
// position (fallback), both operating on the live iframe document.

import * as textPosition from "dom-anchor-text-position";
import * as textQuote from "dom-anchor-text-quote";
import type { Anchor } from "../types";

/** Walk up from a node to the nearest ancestor Element that carries an id. */
function nearestIdElement(node: Node | null): Element | null {
  let el: Element | null =
    node && node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node?.parentElement ?? null;
  while (el) {
    if (el.id) return el;
    el = el.parentElement;
  }
  return null;
}

/** Resolve the root element an Anchor is measured against. */
function rootForElementId(iframeDoc: Document, elementId: string): Element | null {
  if (elementId) {
    return iframeDoc.getElementById(elementId);
  }
  return iframeDoc.body ?? iframeDoc.documentElement;
}

/**
 * Describe the current selection inside the iframe document as an Anchor.
 * Returns null when there is no usable (non-collapsed, non-empty) selection.
 */
export function describeAnchorFromSelection(
  iframeDoc: Document
): Anchor | null {
  const sel = iframeDoc.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  if (range.collapsed) return null;
  if (range.toString().trim().length === 0) return null;

  const idEl = nearestIdElement(range.commonAncestorContainer);
  const elementId = idEl?.id ?? "";
  const root = rootForElementId(iframeDoc, elementId);
  if (!root) return null;

  const quote = textQuote.fromRange(root, range);
  const position = textPosition.fromRange(root, range);

  return {
    elementId,
    exact: quote.exact,
    prefix: quote.prefix ?? "",
    suffix: quote.suffix ?? "",
    start: position.start,
    end: position.end,
  };
}

/**
 * Rehydrate an Anchor into a live Range within the iframe document.
 * Prefers the text-quote selector; falls back to the text-position offsets.
 * Returns null if the anchored text can no longer be located.
 */
export function rangeFromAnchor(
  iframeDoc: Document,
  anchor: Anchor
): Range | null {
  const root = rootForElementId(iframeDoc, anchor.elementId);
  if (!root) return null;

  try {
    const byQuote = textQuote.toRange(root, {
      exact: anchor.exact,
      prefix: anchor.prefix,
      suffix: anchor.suffix,
    });
    if (byQuote) return byQuote;
  } catch {
    // fall through to position fallback
  }

  try {
    return textPosition.toRange(root, { start: anchor.start, end: anchor.end });
  } catch {
    return null;
  }
}
